import type { FBQConfig, FBQEventParams, FBQSubscribeOptions, FBQTrackOptions } from './types';
import { isBrowser, logDebug } from '../core/utils';
import { DEFAULT_FBQ_PIXEL_ID, FBQ_SUBSCRIPTION_MAP } from './const';

/**
 * Facebook Pixel
 */
class FacebookPixel {
  private pixelId: string = DEFAULT_FBQ_PIXEL_ID;
  private initialized: boolean = false;
  private debug: boolean = false;

  /**
   * 初始化 Facebook 像素
   * @param config Facebook 像素配置
   */
  init(config?: FBQConfig): void {
    if (!isBrowser()) {
      logDebug('FBQ initialization skipped: not in browser environment');
      return;
    }

    if (this.initialized) {
      logDebug('FBQ already initialized');
      return;
    }

    if (config?.pixelId) {
      this.pixelId = config.pixelId;
    }

    if (config?.debug) {
      this.debug = config.debug;
    }

    this.injectScript();
    this.injectNoScript();
    this.initialized = true;

    logDebug('FBQ initialized');
  }

  /**
   * 注入 Meta Pixel 脚本
   */
  private injectScript(): void {
    const script = document.createElement('script');
    script.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '${this.pixelId}');
      fbq('track', 'PageView');
    `;
    document.head.appendChild(script);
    script.remove();
  }

  /**
   * 注入 NoScript 标签
   */
  private injectNoScript(): void {
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${this.pixelId}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }

  /**
   * 触发自定义事件
   * @param eventName 事件名称
   * @param params 事件参数
   * @param options 选项参数
   */
  track(eventName: string, params?: FBQEventParams, options?: FBQTrackOptions): void {
    if (!this.initialized) {
      logDebug('FBQ not initialized, skipping event', { eventName, params });
      return;
    }

    if (!isBrowser() || !window.fbq) {
      logDebug('FBQ not available', { eventName, params });
      return;
    }

    window.fbq('track', eventName, params, options);

    if (this.debug) {
      logDebug('Event tracked', { eventName, params, options });
    }
  }

  /**
   * 触发完成注册事件
   * @param params 事件参数，默认 value 为 1
   */
  completeRegistration(params?: { value?: number }): void {
    this.track('CompleteRegistration', { value: 1, ...params });
  }

  /**
   * 触发订阅事件
   * @param subscriptionKey 订阅套餐类型
   */
  subscribe(subscriptionKey: FBQSubscribeOptions): void {
    const subscriptionData = FBQ_SUBSCRIPTION_MAP[subscriptionKey];
    if (!subscriptionData) {
      return;
    }

    this.track('Subscribe', {
      value: subscriptionData.value,
      currency: 'USD',
      content_ids: [subscriptionKey],
      content_name: subscriptionData.content_name,
    });
  }

  /**
   * 触发页面浏览事件
   */
  pageView(): void {
    this.track('PageView');
  }

  /**
   * 获取初始化状态
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * 获取 Pixel ID
   */
  getPixelId(): string {
    return this.pixelId;
  }
}

export default FacebookPixel;
